"use client";

import { useMemo } from "react";
import { useHotelQuery } from "@/hooks/url-state/useHotelQuery";
import { useRentalQuery } from "@/hooks/url-state/useRentalQuery";
import { useSpaQuery } from "@/hooks/url-state/useSpaQuery";
import { useActivityQuery } from "@/hooks/url-state/useActivityQuery";
import type { MainServiceLabel } from "@/components/home/hero/constants";
import {
  type SearchServiceKey,
  toSearchServiceKey,
} from "@/components/search-form/search.modules.type";
import dayjs from "dayjs";
import type { DateRange } from "react-day-picker";

export type ServiceIdentifier = SearchServiceKey | MainServiceLabel | string;

/**
 * Consolidates all service-specific URL state into a single, unified API.
 * Uses tuple mappings (no switch-case or ternary ladders) for clean lookup.
 */
export function useSearchModuleQuery(
  activeService: ServiceIdentifier | undefined,
) {
  const hotel = useHotelQuery();
  const rental = useRentalQuery();
  const spa = useSpaQuery();
  const activity = useActivityQuery();

  const currentServiceKey = toSearchServiceKey(activeService);

  // Service lookup tuple map - pure JS tuples, NO switch-cases or nested ternaries
  const serviceState = useMemo(() => {
    const isSingleDateService = currentServiceKey !== "hotels";

    // Tuple of [dateString, setDateFn]
    const singleDateMap: Record<
      SearchServiceKey,
      readonly [string | undefined, (date?: Date) => unknown]
    > = {
      hotels: [hotel.start, () => {}],
      rentals: [rental.start, rental.setDate],
      spa_msg: [spa.date, spa.setDate],
      tour_act: [activity.date, activity.setDate],
      discover: [undefined, () => {}],
    };

    // Tuple of [startString, endString, setRangeFn]
    const rangeDateMap: Record<
      SearchServiceKey,
      readonly [
        string | undefined,
        string | undefined,
        (from?: Date, to?: Date) => void,
      ]
    > = {
      hotels: [hotel.start, hotel.end, hotel.setRange],
      rentals: [rental.start, rental.end, rental.setRange],
      spa_msg: [undefined, undefined, () => {}],
      tour_act: [undefined, undefined, () => {}],
      discover: [undefined, undefined, () => {}],
    };

    // Common location & search state map
    const commonQueryMap = {
      hotels: hotel,
      rentals: rental,
      spa_msg: spa,
      tour_act: activity,
      discover: hotel,
    } as const;

    const common = commonQueryMap[currentServiceKey] ?? hotel;
    const [date, setDate] = singleDateMap[currentServiceKey];
    const [start, end, setRange] = rangeDateMap[currentServiceKey];

    return {
      common,
      date: date ?? "",
      setDate,
      start: start ?? "",
      end: end ?? "",
      setRange,
      isSingleDate: isSingleDateService,
    };
  }, [currentServiceKey, hotel, rental, spa, activity]);

  const { common, date, setDate, start, end, setRange, isSingleDate } =
    serviceState;

  const locationId = common.locationId ?? "";
  const search = common.search ?? "";
  const lat = common.lat;
  const lng = common.lng;

  const hasCoordinates = Boolean(lat && lng);
  const hasSearch = Boolean(search.trim());
  const hasLocation = Boolean(locationId || hasCoordinates || hasSearch);

  const singleDateValue: Date | undefined =
    date && dayjs(date).isValid() ? dayjs(date).toDate() : undefined;

  const rangeDateValue: DateRange | undefined =
    start || end
      ? {
          from: start ? dayjs(start).toDate() : undefined,
          to: end ? dayjs(end).toDate() : undefined,
        }
      : undefined;

  return {
    serviceKey: currentServiceKey,
    locationId,
    search,
    lat,
    lng,
    hasSearch,
    hasLocation,
    hasCoordinates,
    setSearch: common.setSearch,
    setLocationId: common.setLocationId,
    setCoordinates: common.setCoordinates,
    isSingleDate,
    date,
    singleDateValue,
    rangeDateValue,
    setRange,
    setDate,
    start,
    end,

    // Hotels
    adults: hotel.adults,
    children: hotel.children,
    rooms: hotel.rooms,
    setTravelers: hotel.setTravelers,
    setSearchState: hotel.setSearchState,

    // Rentals
    vehicleType: rental.vehicleType,
    pickupType: rental.pickupType,
    setVehicleType: rental.setVehicleType,
    setPickupType: rental.setPickupType,
  };
}
